// CSV schema inference for ontology data sources.
// Extends the backtest CSV parser (src/lib/backtest/csvParser.ts) with
// null_rate, uniqueness, and samples metrics per column.
// Agent C will use this during the upload wizard; Agent D uses it server-side.

import { parseCSV } from "@/lib/backtest/csvParser";
import type { ColumnSchema, DataSourceSchema, SniffedType } from "@/types/ontology";
import { computeHeaderSignature } from "./headerSignature";

// Maximum rows to sample for type sniffing + uniqueness calculation.
const SAMPLE_LIMIT = 1000;

// Maximum samples to retain per column.
const MAX_SAMPLES = 5;

// Maximum character length per sample value.
const SAMPLE_MAX_CHARS = 200;

/**
 * Infer schema from a parsed CSV (headers + rows).
 * Works on up to `SAMPLE_LIMIT` rows for speed; uniqueness is computed over the sample.
 *
 * @returns DataSourceSchema including per-column metadata and the header_signature.
 */
export async function inferSchema(
  headers: string[],
  rows: Record<string, string>[]
): Promise<DataSourceSchema> {
  const sample = rows.slice(0, SAMPLE_LIMIT);
  const totalRows = sample.length;

  const columns: ColumnSchema[] = headers.map((name) => {
    const values = sample.map((row) => (row[name] ?? "").trim());

    const nonEmpty = values.filter((v) => v !== "");
    const emptyCount = values.length - nonEmpty.length;
    const null_rate = totalRows > 0 ? emptyCount / totalRows : 0;

    // Uniqueness over non-empty values.
    const distinctCount = new Set(nonEmpty).size;
    const uniqueness = nonEmpty.length > 0 ? distinctCount / nonEmpty.length : 0;

    // Samples: first MAX_SAMPLES distinct non-empty values, truncated.
    const seen = new Set<string>();
    const samples: string[] = [];
    for (const v of nonEmpty) {
      if (!seen.has(v)) {
        seen.add(v);
        samples.push(v.slice(0, SAMPLE_MAX_CHARS));
        if (samples.length >= MAX_SAMPLES) break;
      }
    }

    // Type sniff over all non-empty values in the sample.
    const sniffed_type = sniffType(nonEmpty);

    return { name, sniffed_type, null_rate, uniqueness, samples };
  });

  const header_signature = await computeHeaderSignature(headers);

  return { columns, header_signature };
}

/**
 * Parse raw CSV text and infer its schema.
 * Convenience wrapper that calls parseCSV then inferSchema.
 */
export async function inferSchemaFromText(csvText: string): Promise<{
  headers: string[];
  rows: Record<string, string>[];
  schema: DataSourceSchema;
}> {
  const { headers, rows } = parseCSV(csvText);
  const schema = await inferSchema(headers, rows);
  return { headers, rows, schema };
}

// ── Type sniffing ─────────────────────────────────────────────────────────────

/**
 * Sniff the most specific type that fits all non-empty values in the column.
 * Order: date > number > bool > string (fallback).
 */
function sniffType(nonEmpty: string[]): SniffedType {
  if (nonEmpty.length === 0) return "string";

  if (nonEmpty.every(isDate)) return "date";
  if (nonEmpty.every(isNumber)) return "number";
  if (nonEmpty.every(isBool)) return "bool";
  return "string";
}

// ISO 8601 + common US date patterns.
const DATE_RE = /^(\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{2,4})(T[\d:.Z+-]*)?$/;

function isDate(v: string): boolean {
  if (!DATE_RE.test(v)) return false;
  return !isNaN(new Date(v).getTime());
}

function isNumber(v: string): boolean {
  // Strip common currency prefixes and separators before testing.
  const cleaned = v.replace(/[$£€¥,\s]/g, "");
  return cleaned !== "" && !isNaN(Number(cleaned));
}

const BOOL_VALUES = new Set(["true", "false", "yes", "no", "1", "0", "on", "off"]);

function isBool(v: string): boolean {
  return BOOL_VALUES.has(v.toLowerCase());
}
